// Universal Mega Menu Component v7.0 - Works in Shopify & React Apps
// Compatible with: Shopify Liquid, Next.js, Create React App, and vanilla JS
(function(global) {
  'use strict';
  
  console.log("Loading Universal Mega Menu Component v7.0");

  // Universal component factory that works in all environments
  function createMegaMenuComponent() {
    // Detect React from multiple possible sources
    var React = null;
    
    // Check for React in different locations
    if (typeof window !== 'undefined') {
      React = window.React || (window.app && window.app.React);
    }
    if (!React && typeof global !== 'undefined') {
      React = global.React;
    }
    if (!React && typeof globalThis !== 'undefined') {
      React = globalThis.React;
    }
    
    // If no React found, try to use from props (for React apps that pass it)
    if (!React && typeof arguments !== 'undefined' && arguments[0] && arguments[0].React) {
      React = arguments[0].React;
    }

    // Component implementation
    var MegaMenuComponent = function(providedReact) {
      // Allow React to be passed as parameter for React app usage
      var R = providedReact || React;
      
      if (!R) {
        console.error('React not available - component cannot render');
        console.log('Please ensure React is available globally or pass it as a parameter');
        return null;
      }

      var useState = R.useState;
      var useEffect = R.useEffect;
      var useCallback = R.useCallback;
      var useRef = R.useRef;
      var useMemo = R.useMemo;
      var createElement = R.createElement;

      return function MegaMenu() {
        // State management
        var _a = useState(null), activeMenu = _a[0], setActiveMenu = _a[1];
        var _b = useState(false), isMenuOpen = _b[0], setIsMenuOpen = _b[1];
        var _c = useState(false), isMobile = _c[0], setIsMobile = _c[1];
        var _d = useState(null), activeCategory = _d[0], setActiveCategory = _d[1];
        var _e = useState(false), mobileMenuOpen = _e[0], setMobileMenuOpen = _e[1];
        var _f = useState(null), mobileActiveCategory = _f[0], setMobileActiveCategory = _f[1];
        var _g = useState(new Set()), mobileExpandedItems = _g[0], setMobileExpandedItems = _g[1];

        // Refs for performance and cleanup
        var menuTimeoutRef = useRef(null);
        var dropdownRef = useRef(null);
        var mobileMenuRef = useRef(null);

        // Get menu data - handles both Shopify and React app data sources
        var menuData = useMemo(function() {
          var data = null;
          
          // Check multiple possible data sources
          // 1. Shopify global (megaMenuData)
          if (typeof window !== 'undefined' && window.megaMenuData && window.megaMenuData.data && window.megaMenuData.data.megamenu) {
            data = window.megaMenuData.data.megamenu;
            console.log('Menu data loaded from megaMenuData');
          } 
          // 2. Custom global (customMenuData)
          else if (typeof window !== 'undefined' && window.customMenuData && window.customMenuData.data && window.customMenuData.data.megamenu) {
            data = window.customMenuData.data.megamenu;
            console.log('Menu data loaded from customMenuData');
          }
          // 3. Check for data passed via props/context (for React apps)
          else if (typeof window !== 'undefined' && window.__MENU_DATA__) {
            data = window.__MENU_DATA__;
            console.log('Menu data loaded from __MENU_DATA__');
          }
          
          if (data) {
            console.log('Menu data loaded successfully:', data.length, 'categories');
            return data;
          }
          
          console.log('Menu data not yet available');
          return [];
        }, []);

        // Enhanced URL generation - handles all URL types
        var getItemUrl = useCallback(function(urlSetting) {
          if (!urlSetting) return '#';
          
          try {
            // Direct string URL
            if (typeof urlSetting === 'string') {
              return urlSetting;
            }
            
            // Shopify URL structure
            if (urlSetting.type) {
              var type = urlSetting.type;
              if (type.id === 'collection' && urlSetting.collection) {
                return '/collections/' + urlSetting.collection.handle;
              } else if (type.id === 'home') {
                return '/';
              } else if (type.id === 'blog' && urlSetting.blog) {
                return '/blogs/' + urlSetting.blog.handle;
              } else if (type.id === 'page' && urlSetting.page) {
                return '/pages/' + urlSetting.page.handle;
              } else if (type.id === 'external' && urlSetting.external_url) {
                return urlSetting.external_url;
              }
            }
            
            return '#';
          } catch (error) {
            console.warn('Error generating URL:', error);
            return '#';
          }
        }, []);

        // Menu interactions for desktop
        var handleMenuEnter = useCallback(function(menuId) {
          if (menuTimeoutRef.current) {
            clearTimeout(menuTimeoutRef.current);
          }
          setActiveMenu(menuId);
          setIsMenuOpen(true);
          setActiveCategory(null);

          // Track analytics if available
          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'menu_open', {
              'event_category': 'navigation',
              'event_label': menuId
            });
          }
        }, []);

        var handleMenuLeave = useCallback(function() {
          menuTimeoutRef.current = setTimeout(function() {
            setIsMenuOpen(false);
            setActiveMenu(null);
            setActiveCategory(null);
          }, 200);
        }, []);

        var handleCategoryHover = useCallback(function(categoryId) {
          setActiveCategory(categoryId);
        }, []);

        // Mobile menu interactions
        var toggleMobileMenu = useCallback(function() {
          setMobileMenuOpen(function(prev) {
            var next = !prev;
            if (typeof document !== 'undefined') {
              if (!prev) {
                document.body.style.overflow = 'hidden';
              } else {
                document.body.style.overflow = '';
              }
            }
            return next;
          });
        }, []);

        var closeMobileMenu = useCallback(function() {
          setMobileMenuOpen(false);
          if (typeof document !== 'undefined') {
            document.body.style.overflow = '';
          }
          setMobileExpandedItems(new Set());
          setMobileActiveCategory(null);
        }, []);

        var toggleMobileCategory = useCallback(function(categoryId) {
          setMobileExpandedItems(function(prev) {
            var newSet = new Set(prev);
            if (newSet.has(categoryId)) {
              newSet.delete(categoryId);
            } else {
              newSet.add(categoryId);
            }
            return newSet;
          });
        }, []);

        // Responsive handling
        useEffect(function() {
          if (typeof window === 'undefined') return;
          
          var timeoutId;
          var handleResize = function() {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(function() {
              var isMobileNow = window.innerWidth < 1024;
              setIsMobile(isMobileNow);
              if (!isMobileNow && mobileMenuOpen) {
                closeMobileMenu();
              }
            }, 100);
          };
          
          handleResize();
          window.addEventListener('resize', handleResize);
          
          return function() {
            window.removeEventListener('resize', handleResize);
            if (timeoutId) clearTimeout(timeoutId);
          };
        }, [mobileMenuOpen, closeMobileMenu]);

        // Listen for mobile menu toggle from Shopify header
        useEffect(function() {
          if (typeof document === 'undefined') return;
          
          var handleMobileToggle = function(e) {
            if (e.target && e.target.closest) {
              if (e.target.closest('.filter-button') || e.target.closest('[data-action="open-filters"]')) {
                return;
              }
              if (e.target.closest('.toggle-menu') || e.target.closest('[data-action="toggle-browse"]')) {
                e.preventDefault();
                e.stopPropagation();
                toggleMobileMenu();
              }
            }
          };
          
          document.addEventListener('click', handleMobileToggle, true);
          
          return function() {
            document.removeEventListener('click', handleMobileToggle, true);
          };
        }, [toggleMobileMenu]);

        // Cleanup
        useEffect(function() {
          return function() {
            if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = '';
            }
          };
        }, []);

        // Render dropdown panel
        var renderDropdownPanel = useCallback(function(menuItem) {
          if (!menuItem.menus || menuItem.menus.length === 0) return null;

          var currentCategoryId = activeCategory || (menuItem.menus[0] ? menuItem.menus[0].id : null);
          var selectedCategory = (menuItem.menus || []).find(function(cat) { 
            return cat.id === currentCategoryId; 
          });

          return createElement(
            'div',
            {
              ref: dropdownRef,
              className: 'ca-dropdown-panel',
              onMouseEnter: function() {
                if (menuTimeoutRef.current) {
                  clearTimeout(menuTimeoutRef.current);
                }
                setIsMenuOpen(true);
              },
              onMouseLeave: handleMenuLeave
            },
            // Left column - categories
            createElement(
              'div',
              { className: 'ca-dropdown-main' },
              createElement('h3', { className: 'ca-dropdown-title' }, 
                menuItem.setting && menuItem.setting.title ? menuItem.setting.title : 'Categories'
              ),
              (menuItem.menus || []).map(function(category) {
                var isActiveCat = currentCategoryId === category.id;
                var linkClass = 'ca-dropdown-category' + (isActiveCat ? ' ca-active' : '');
                return createElement(
                  'div',
                  { key: category.id, className: linkClass },
                  createElement(
                    'a',
                    {
                      href: getItemUrl(category.setting && category.setting.url),
                      className: 'ca-dropdown-category-link',
                      onMouseEnter: function() { handleCategoryHover(category.id); }
                    },
                    (category.setting && category.setting.title) ? category.setting.title : 'Category'
                  )
                );
              })
            ),
            // Right column - subcategories
            (selectedCategory && selectedCategory.menus && selectedCategory.menus.length > 0) ?
              createElement(
                'div',
                { className: 'ca-dropdown-sub' },
                createElement('h3', { className: 'ca-dropdown-sub-title' }, 
                  (selectedCategory.setting && selectedCategory.setting.title) ? 
                    selectedCategory.setting.title : 'Items'
                ),
                (selectedCategory.menus || []).map(function(subItem) {
                  return createElement(
                    'a',
                    {
                      key: subItem.id,
                      href: getItemUrl(subItem.setting && subItem.setting.url),
                      className: 'ca-dropdown-sub-link'
                    },
                    (subItem.setting && subItem.setting.title) ? subItem.setting.title : 'Item'
                  );
                })
              )
            : null
          );
        }, [activeCategory, getItemUrl, handleMenuLeave, handleCategoryHover]);

        // Render mobile menu item
        var renderMobileMenuItem = useCallback(function(item, level) {
          if (level === void 0) level = 0;
          
          var hasSubMenu = item.menus && item.menus.length > 0;
          var isExpanded = mobileExpandedItems.has(item.id);
          var title = (item.setting && item.setting.title) ? item.setting.title : 'Menu Item';
          var isCampsite = !!(title && (title.indexOf('Campsite') !== -1 || title.indexOf('Find') !== -1)) || 
                          item.id === 'find-campsites';

          var liClass = 'mobile-menu-item level-' + level + (isCampsite ? ' campsite-item' : '');
          var expandBtnClass = 'mobile-expand-btn' + (isExpanded ? ' expanded' : '');
          var linkClass = 'mobile-menu-link' + (isCampsite ? ' campsite-link' : '');

          return createElement(
            'li',
            { key: item.id, className: liClass },
            hasSubMenu ?
              createElement(
                'div',
                { className: 'mobile-menu-item-wrapper' },
                createElement(
                  'a',
                  { 
                    href: getItemUrl(item.setting && item.setting.url), 
                    className: 'mobile-menu-link' 
                  },
                  title
                ),
                createElement(
                  'button',
                  {
                    className: expandBtnClass,
                    onClick: function() { toggleMobileCategory(item.id); },
                    'aria-label': 'Expand ' + title
                  },
                  isExpanded ? '-' : '+'
                )
              )
            :
              createElement(
                'a',
                {
                  href: getItemUrl(item.setting && item.setting.url),
                  className: linkClass,
                  onClick: isCampsite ? undefined : closeMobileMenu
                },
                title
              ),
            (hasSubMenu && isExpanded) ?
              createElement(
                'ul',
                { className: 'mobile-submenu' },
                (item.menus || []).map(function(subItem) { 
                  return renderMobileMenuItem(subItem, level + 1); 
                })
              )
            : null
          );
        }, [mobileExpandedItems, toggleMobileCategory, getItemUrl, closeMobileMenu]);

        // Don't render if no menu data
        if (!menuData || menuData.length === 0) {
          console.log('No menu data available, not rendering menu');
          return null;
        }

        // Mobile menu render
        if (isMobile) {
          var wrapperClass = 'ca-mobile-menu-wrapper' + (mobileMenuOpen ? ' menu-open' : '');
          var panelClass = 'ca-mobile-menu-panel' + (mobileMenuOpen ? ' open' : '');
          
          return createElement(
            'div',
            { className: wrapperClass },
            mobileMenuOpen ? createElement('div', { 
              className: 'ca-mobile-overlay', 
              onClick: closeMobileMenu 
            }) : null,
            createElement(
              'div',
              { ref: mobileMenuRef, className: panelClass },
              createElement(
                'div',
                { className: 'ca-mobile-menu-header' },
                createElement('h2', null, 'Menu'),
                createElement(
                  'button',
                  { 
                    className: 'ca-mobile-close-btn', 
                    onClick: closeMobileMenu, 
                    'aria-label': 'Close menu' 
                  },
                  'x'
                )
              ),
              createElement(
                'div',
                { className: 'ca-mobile-menu-content' },
                createElement(
                  'ul',
                  { className: 'ca-mobile-menu-list' },
                  menuData.map(function(item) { return renderMobileMenuItem(item); })
                )
              )
            )
          );
        }

        // Desktop menu render
        return createElement(
          'div',
          { className: 'ca-enhanced-menu' },
          createElement(
            'div',
            { className: 'ca-menu-container' },
            createElement(
              'nav',
              { className: 'ca-main-navigation' },
              menuData.map(function(item) {
                var hasDropdown = item.menus && item.menus.length > 0;
                var isActive = activeMenu === item.id && isMenuOpen;
                var title = (item.setting && item.setting.title) ? item.setting.title : 'Menu Item';
                var isCampsite = !!(title && (title.indexOf('Campsite') !== -1 || 
                                 title.indexOf('Find') !== -1)) || 
                                 item.id === 'find-campsites';

                var itemClass = 'ca-nav-item' +
                  (hasDropdown ? ' ca-has-dropdown' : '') +
                  (isActive ? ' ca-active' : '') +
                  (isCampsite ? ' ca-campsite' : '');

                var arrowClass = 'ca-dropdown-arrow' + (isActive ? ' ca-active' : '');

                return createElement(
                  'div',
                  {
                    key: item.id,
                    className: itemClass,
                    onMouseEnter: hasDropdown ? function() { handleMenuEnter(item.id); } : undefined,
                    onMouseLeave: hasDropdown ? handleMenuLeave : undefined
                  },
                  createElement(
                    'a',
                    { 
                      href: getItemUrl(item.setting && item.setting.url), 
                      className: 'ca-nav-link' 
                    },
                    title,
                    hasDropdown ? createElement('span', { className: arrowClass }, 
                      String.fromCharCode(9660)
                    ) : null
                  ),
                  (hasDropdown && isActive) ? renderDropdownPanel(item) : null
                );
              })
            )
          )
        );
      };
    };

    return MegaMenuComponent;
  }

  // Create the component factory
  var MegaMenuFactory = createMegaMenuComponent();

  // Export for different environments
  // 1. Shopify/Browser global
  if (typeof window !== 'undefined') {
    window.MegaMenu = MegaMenuFactory;
    window.CampingAustraliaMegaMenu = MegaMenuFactory; // Legacy support
    window.createMegaMenu = MegaMenuFactory;
    
    // Auto-initialize for Shopify if React is available
    if (window.React) {
      window.MegaMenuComponent = MegaMenuFactory(window.React);
    }
  }

  // 2. CommonJS (Node.js/older bundlers)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = MegaMenuFactory;
    module.exports.createMegaMenu = MegaMenuFactory;
    module.exports.default = MegaMenuFactory;
  }

  // 3. AMD (RequireJS)
  if (typeof define === 'function' && define.amd) {
    define([], function() {
      return MegaMenuFactory;
    });
  }

  // 4. ES6 Modules (for modern bundlers)
  if (typeof exports !== 'undefined') {
    exports.MegaMenu = MegaMenuFactory;
    exports.createMegaMenu = MegaMenuFactory;
    exports.default = MegaMenuFactory;
  }

  console.log("Universal Mega Menu Component v7.0 ready!");
  console.log("Usage in Shopify: window.MegaMenu(window.React)");
  console.log("Usage in React App: import MegaMenu from './mega-menu.js'; const Menu = MegaMenu(React);");

})(typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this);